import { edgeAuthHeader } from '@/lib/edgeAuth';

export type LLMSchema = 'ranking-low' | 'ranking-medium' | 'reevaluate';

export interface LLMAttempt { provider: string; model: string; reason: string }

export interface LLMResult {
  text: string;
  provider: string;
  model: string;
}

/** Thrown when llm-api answers with an error. `attempts` names every chain
 *  entry that failed, so Sentry can group by provider and model. */
export class LLMError extends Error {
  attempts: LLMAttempt[];
  status: number;
  constructor(message: string, status: number, attempts: LLMAttempt[]) {
    super(message);
    this.name = 'LLMError';
    this.status = status;
    this.attempts = attempts;
  }
}

interface LLMCallOptions {
  prompt: string;
  systemPrompt: string;
  maxTokens: number;
  schema: LLMSchema;
}

/** The one client for the llm-api ranking endpoint. */
export async function callLLM(opts: LLMCallOptions): Promise<LLMResult> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) throw new Error('Supabase configuration missing');

  const response = await fetch(`${supabaseUrl}/functions/v1/llm-api`, {
    method: 'POST',
    headers: { Authorization: await edgeAuthHeader(), 'Content-Type': 'application/json' },
    signal: AbortSignal.timeout(150_000),
    body: JSON.stringify({
      prompt: opts.prompt,
      systemPrompt: opts.systemPrompt,
      maxTokens: opts.maxTokens,
      schema: opts.schema,
    }),
  });

  const data = await response.json().catch(() => ({})) as {
    content?: Array<{ text: string }>;
    provider?: string;
    model?: string;
    error?: string;
    attempts?: LLMAttempt[];
  };
  if (!response.ok || data.error) {
    throw new LLMError(data.error || `LLM API request failed (${response.status})`, response.status, data.attempts ?? []);
  }
  return { text: data.content?.[0]?.text ?? '', provider: data.provider ?? 'unknown', model: data.model ?? 'unknown' };
}

/** Parse the JSON array llm-api returns. Repairs a truncated array by
 *  dropping the last partial object. Throws when nothing usable remains. */
export function parseJsonArray(text: string): unknown[] {
  let cleaned = text.trim();
  if (cleaned.startsWith('```json')) cleaned = cleaned.slice(7);
  else if (cleaned.startsWith('```')) cleaned = cleaned.slice(3);
  if (cleaned.endsWith('```')) cleaned = cleaned.slice(0, -3);
  cleaned = cleaned.trim();

  try {
    const parsed = JSON.parse(cleaned);
    if (!Array.isArray(parsed)) throw new SyntaxError('LLM returned a non-array');
    return parsed;
  } catch (err) {
    if (!cleaned.startsWith('[')) throw err;
    const lastCloseBrace = cleaned.lastIndexOf('}');
    if (lastCloseBrace === -1) throw err;
    const repaired = JSON.parse(cleaned.substring(0, lastCloseBrace + 1) + ']');
    if (!Array.isArray(repaired) || repaired.length === 0) throw err;
    console.warn(`Repaired truncated LLM JSON: recovered ${repaired.length} items`);
    return repaired;
  }
}

/** Sentry fingerprint for a ranking substitution: one issue per failing
 *  provider and model, so a retirement shows as one issue with a count. */
export function llmFingerprint(scope: string, err: unknown): string[] {
  if (err instanceof LLMError && err.attempts.length > 0) {
    const last = err.attempts[err.attempts.length - 1];
    return [scope, last.provider, last.model, last.reason.split(':')[0]];
  }
  return [scope, err instanceof Error ? err.name : 'unknown'];
}
